import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import { api } from '../api/api';
import { toast } from 'sonner';

const LandingPage = ({ userId }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    marketingOptIn: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.createSubscriber(formData);
      localStorage.setItem('ugandaInsiderEmail', formData.email);
      localStorage.setItem('ugandaInsiderFirstName', formData.firstName);
      toast.success('Successfully subscribed!');
      
      // Navigate to first offer
      setTimeout(() => {
        navigate('/offer-1');
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  const SubscriptionForm = () => (
    <div className="bg-gradient-to-br from-purple-50 to-teal-50 rounded-lg shadow-xl p-8">
      <h3 className="text-2xl font-bold mb-6 text-center" style={{color: '#3D2817'}}>
        Subscribe Now – Support Conservation and Sustainability in Uganda
      </h3>
      
      <form onSubmit={handleSubmit} className="max-w-md mx-auto" data-testid="subscription-form">
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2" htmlFor="firstName">
            First Name *
          </label>
          <input
            type="text"
            id="firstName"
            required
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition"
            value={formData.firstName}
            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
            data-testid="first-name-input"
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2" htmlFor="email">
            Email *
          </label>
          <input
            type="email"
            id="email"
            required
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            data-testid="email-input"
          />
        </div>

        <div className="mb-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              checked={formData.marketingOptIn}
              onChange={(e) => setFormData({...formData, marketingOptIn: e.target.checked})}
              data-testid="marketing-optin-checkbox"
            />
            <span className="ml-2 text-gray-700">I agree to receive marketing emails</span>
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-lg font-bold text-white text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
          style={{backgroundColor: '#C85A3E'}}
          data-testid="subscribe-button"
        >
          {loading ? 'Subscribing...' : 'Subscribe Now'}
        </button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{color: '#3D2817'}}>
              Discover the Pearl of Africa
            </h2>
            <p className="text-xl text-gray-700 mb-6">
              Subscribe to Uganda Insider - Your Monthly Digital Magazine
            </p>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <VideoPlayer videoUrl="https://youtu.be/kyrTeqpnl6Q" />
          </div>

          {/* Benefits Section */}
          <div className="bg-white rounded-lg shadow-xl p-8 mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center" style={{color: '#C85A3E'}}>
              Why Subscribe to Uganda Insider?
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0" style={{backgroundColor: '#D4A948'}}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Exclusive Content</h4>
                  <p className="text-gray-600">Get insider tips, travel guides, and cultural insights delivered monthly</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0" style={{backgroundColor: '#2C7A9F'}}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Discover Hidden Gems</h4>
                  <p className="text-gray-600">Explore Uganda's breathtaking landscapes and vibrant culture</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0" style={{backgroundColor: '#8B3A9B'}}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Local Expert Insights</h4>
                  <p className="text-gray-600">Connect with local experts and authentic experiences</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0" style={{backgroundColor: '#FF8C42'}}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Special Offers</h4>
                  <p className="text-gray-600">Access exclusive deals on tours, accommodations, and experiences</p>
                </div>
              </div>
            </div>

            {/* Feature Image */}
            <div className="w-full rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_sequence-builder-2/artifacts/dert9edy_Gorillas%2C%20Wildlife%2C%20Big%20Five%2C%20and%20Chimps%20Experience%20%2814%29.jpg"
                alt="Mountain Gorilla in Uganda - Wildlife Experience"
                className="w-full h-auto object-cover"
                loading="lazy"
                data-testid="feature-image"
              />
            </div>
          </div>

          {/* First Subscription Form */}
          <SubscriptionForm />

          {/* Panel 1: Eco-Tourism */}
          <div className="my-12 bg-white rounded-lg shadow-xl overflow-hidden" style={{backgroundColor: '#8B9E7D'}}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center mb-4">
                  <svg className="w-8 h-8 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                  <h3 className="text-2xl md:text-3xl font-bold" style={{color: '#2C3E1F'}}>Eco-Tourism: Traveling Responsibly</h3>
                </div>
                <p className="text-gray-800 mb-6">
                  We practice eco-tourism by designing safaris that minimize environmental impact while maximizing benefits to local communities and conservation efforts. This means:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Using eco-friendly lodges and camps that promote renewable energy, water conservation, and waste management.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Encouraging low-impact tourism by limiting visitor numbers in sensitive areas to protect fragile ecosystems.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Educating travelers on ethical wildlife interactions, ensuring that animal welfare and natural behaviors are respected.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Supporting conservation-focused tours that give guests an opportunity to learn about sustainability in action.</p>
                  </div>
                </div>
              </div>
              <div className="h-64 md:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sequence-builder-2/artifacts/7x97kggw_Screenshot%202025-11-09%20at%2011.06.33.png"
                  alt="Eco-Tourism - Cheetah in natural habitat"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Panel 2: Wildlife Preservation */}
          <div className="my-12 bg-white rounded-lg shadow-xl overflow-hidden" style={{backgroundColor: '#8B9E7D'}}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="h-64 md:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sequence-builder-2/artifacts/0gr1356k_Screenshot%202025-11-09%20at%2011.06.58.png"
                  alt="Wildlife Preservation - Mountain Gorilla"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center mb-4">
                  <svg className="w-8 h-8 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                  <h3 className="text-2xl md:text-3xl font-bold" style={{color: '#2C3E1F'}}>Wildlife Preservation: Protecting East Africa's Natural Heritage</h3>
                </div>
                <p className="text-gray-800 mb-6">
                  East Africa's incredible biodiversity is under constant threat from poaching, habitat destruction, and climate change. At Manovah Eco Safaris, we actively contribute to wildlife conservation through:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Partnering with local conservation organizations to support anti-poaching efforts and wildlife rescue programs.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Funding reforestation projects that restore habitats for endangered species like gorillas, chimpanzees, and elephants.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Encouraging responsible game drives that prioritize ethical wildlife viewing without disrupting animal behavior.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Educating tourists on the importance of biodiversity and conservation to inspire long-term commitment to protecting nature.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Panel 3: Community Involvement */}
          <div className="my-12 bg-white rounded-lg shadow-xl overflow-hidden" style={{backgroundColor: '#8B9E7D'}}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center mb-4">
                  <svg className="w-8 h-8 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                  <h3 className="text-2xl md:text-3xl font-bold" style={{color: '#2C3E1F'}}>Community Involvement: Giving Back to Local People</h3>
                </div>
                <p className="text-gray-800 mb-6">
                  We believe that conservation is only successful when local communities are part of the solution. Our commitment to community-based tourism ensures that people living around national parks benefit from tourism in meaningful ways. We support:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Education programs that teach local communities about conservation and sustainable resource use.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Employment opportunities, hiring local guides, rangers, and staff to provide income and skills development.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Cultural tourism initiatives, where guests engage with traditional communities, promoting cultural exchange and economic empowerment.</p>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-700 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-800">Giving back programs, where part of every safari booking goes towards funding community projects like schools, healthcare, and clean water initiatives.</p>
                  </div>
                </div>
              </div>
              <div className="h-64 md:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sequence-builder-2/artifacts/8cdqfgat_Screenshot%202025-11-09%20at%2011.07.16.png"
                  alt="Community Involvement - Local People"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Second Subscription Form */}
          <SubscriptionForm />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;